import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { z } from "zod";

export const UnitSchema = z.object({
    id: z.number(),
    "@id": z.string().optional(),
    code: z.string(),
    title: z.string(),
    abbreviation: z.string().optional(),
    definition: z.string().optional().nullable(),
});

const UnitArraySchema = z.array(UnitSchema);

export const unitSelectorApiKey = "unitSelector";

export type Unit = z.infer<typeof UnitSchema>;

type UseUnitSelectorOptions = {
    searchText?: string;
    enabled?: boolean;
    itemsPerPage?: number;
};

export const useUnitSelector = (options: UseUnitSelectorOptions = {}) => {
    const { searchText = "", enabled = true, itemsPerPage = 200 } = options;

    const query = useQuery({
        queryKey: [unitSelectorApiKey, searchText, itemsPerPage],
        queryFn: async () => {
            const params: Record<string, any> = {
                page: 1,
                itemsPerPage,
                order: { title: "ASC" },
            };

            // Add search parameter if searchText is provided
            // API Platform SearchFilter uses the property name as query parameter
            if (searchText && searchText.length > 0) {
                params.title = searchText;
            }

            const response = await sureApiCall.get<{ "hydra:member": unknown; "hydra:totalItems": number }>(
                "/api/units",
                {
                    params,
                },
            );

            return response.data;
        },
        select: (data) => {
            if (!data || !data["hydra:member"]) {
                return {
                    units: [],
                    totalItems: 0,
                };
            }

            const units = data["hydra:member"] || [];
            const totalItems = data["hydra:totalItems"] || 0;

            const parsedUnits = zodParseWithLog(units, UnitArraySchema, unitSelectorApiKey);

            return {
                units: parsedUnits,
                totalItems,
            };
        },
        enabled: enabled,
        staleTime: 30000, // Cache for 30 seconds
        cacheTime: 300000, // Keep in cache for 5 minutes
    });

    return {
        units: query.data?.units || [],
        totalItems: query.data?.totalItems || 0,
        ...query,
    };
};
